/**
 * ColorPicker Component
 *
 * Modal for selecting player color. Displays available color options
 * (excluding colors already used by other players) and allows player
 * to select a new color.
 *
 * Used by:
 * - LobbyPhase (when player clicks their color)
 *
 * Responsibilities:
 * - Display available color options
 * - Show selected color with border highlight
 * - Handle color selection
 * - Confirm/cancel buttons
 * - Portal rendering for modal overlay
 *
 * Dependencies: utils/colors.ts, types/game.types.ts
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS } from '../../../utils/colors';

/**
 * Player color type - matches colors.ts definition
 */
type PlayerColor =
    | 'red'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'cyan'
    | 'blue'
    | 'dark_blue'
    | 'black'
    | 'purple';

export interface ColorPickerProps {
    /** Function to close the modal */
    closeModal: (open: boolean) => void;
    /** Callback when color is confirmed */
    onConfirm: (color: PlayerColor) => void;
    /** Color of player A */
    Acolor: PlayerColor;
    /** Color of player B */
    Bcolor: PlayerColor;
}

/**
 * All available player colors
 */
const colorOptions: PlayerColor[] = [
    'red',
    'orange',
    'yellow',
    'green',
    'cyan',
    'blue',
    'dark_blue',
    'black',
    'purple',
];

/**
 * ColorPicker Component
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
    closeModal,
    onConfirm,
    Acolor,
    Bcolor,
}) => {
    const [selected, setSelected] = useState<PlayerColor | ''>('');

    /**
     * Handle color option click - toggle selection
     */
    const handleClick = (color: PlayerColor): void => {
        if (selected === color) {
            setSelected('');
        } else {
            setSelected(color);
        }
    };

    /**
     * Handle submit - confirm color selection
     */
    const handleSubmit = (): void => {
        if (!selected) {
            return;
        }
        onConfirm(selected);
        closeModal(false);
    };

    /**
     * Handle cancel - close modal without selecting
     */
    const handleCancel = (): void => {
        closeModal(false);
    };

    /**
     * Get style for color option (highlight if selected)
     */
    const getStyle = (color: PlayerColor): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            backgroundColor: COLORS.player_colors[color],
        };

        if (selected === color) {
            return {
                ...baseStyle,
                borderColor: COLORS.selected.ocean,
            };
        }

        return baseStyle;
    };

    return createPortal(
        <div className="color_picker_background">
            <div className="color_picker_container">
                <p className="color_picker_title">Choose your Color!</p>
                <div className="color_picker_options">
                    {colorOptions.map((color) => {
                        // Don't show colors already used by other players
                        if (color !== Acolor && color !== Bcolor) {
                            return (
                                <div
                                    key={color}
                                    className="color_option"
                                    style={getStyle(color)}
                                    onClick={() => handleClick(color)}
                                />
                            );
                        }
                        return null;
                    })}
                </div>
                <div className="color_picker_buttons">
                    <button className="color_picker_button" onClick={handleCancel}>
                        Cancel
                    </button>
                    <button className="color_picker_button" onClick={handleSubmit}>
                        Submit
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ColorPicker;
